import React, { useState, useCallback, useEffect } from 'react';
import { Icon } from '../Icon';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import {
  removeUpload,
  clearCompleted,
  xhrStore,
  abortControllerStore,
} from '../../redux/reducers/uploadReducer';
import { UploadItemComponent } from './UploadItem';

export const UploadManager: React.FC = () => {
  const dispatch = useDispatch();
  const uploads = useSelector((state: RootState) => state.uploads.uploads);

  const [isExpanded, setIsExpanded] = useState(true);

  const handleCancel = useCallback(
    (uploadId: string) => {
      abortControllerStore.get(uploadId)?.abort();
      abortControllerStore.delete(uploadId);
      const xhr = xhrStore.get(uploadId);
      if (xhr) xhr.abort();
      dispatch(removeUpload(uploadId));
    },
    [dispatch]
  );

  const handleRemove = useCallback(
    (uploadId: string) => {
      dispatch(removeUpload(uploadId));
    },
    [dispatch]
  );

  const handleClearCompleted = useCallback(() => {
    dispatch(clearCompleted());
  }, [dispatch]);

  const activeUploads = uploads?.filter((u) => u.status === 'uploading') || [];
  const completedUploads =
    uploads?.filter((u) => u.status === 'completed') || [];
  const failedUploads = uploads?.filter((u) => u.status === 'failed') || [];

  // Auto-clear completed uploads after 5 s (only when nothing is still uploading)
  useEffect(() => {
    if (
      activeUploads.length === 0 &&
      completedUploads.length > 0 &&
      failedUploads.length === 0
    ) {
      const timer = setTimeout(() => {
        handleClearCompleted();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [activeUploads.length, completedUploads.length, failedUploads.length]);

  useEffect(() => {
    if (activeUploads.length > 0) {
      setIsExpanded(true);
    }
  }, [activeUploads.length]);

  if (!uploads?.length) return null;

  return (
    <div className="fixed bottom-1 right-6 z-40 w-96 max-w-[calc(100vw-24px)]">
      {/* Header — expand toggle */}
      <div
        className="flex cursor-pointer items-center justify-between rounded-t-lg border border-gray-200 bg-white p-1 px-3 select-none hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          <Icon
            name="upload"
            className="w-5 h-5 text-primary dark:text-dark-sidebar-text"
          />
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            Uploads ({activeUploads.length})
          </span>
        </div>
        <button
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
        >
          <svg
            className={`w-3 h-3 transition-transform ${
              isExpanded ? '' : 'rotate-180'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className="max-h-96 overflow-y-auto rounded-b-lg border border-t-0 border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
          {activeUploads.length > 0 && (
            <div className="space-y-2 p-3">
              {activeUploads.map((upload) => (
                <UploadItemComponent
                  key={upload.id}
                  item={upload}
                  onRemove={handleCancel}
                  onCancel={handleCancel}
                />
              ))}
            </div>
          )}

          {completedUploads.length > 0 && (
            <div className="space-y-2 border-t border-gray-200 p-3 dark:border-gray-700">
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                Completed
              </p>
              {completedUploads.map((upload) => (
                <UploadItemComponent
                  key={upload.id}
                  item={upload}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}

          {failedUploads.length > 0 && (
            <div className="space-y-2 border-t border-gray-200 p-3 dark:border-gray-700">
              <p className="text-xs font-semibold uppercase text-red-500">
                Failed
              </p>
              {failedUploads.map((upload) => (
                <UploadItemComponent
                  key={upload.id}
                  item={upload}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}

          {completedUploads.length > 0 && (
            <div className="border-t border-gray-200 p-3 dark:border-gray-700">
              <button
                onClick={handleClearCompleted}
                className="w-full text-sm font-medium text-primary-600 hover:text-primary-950"
              >
                Clear Completed
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
